import type {
  Block,
  CustomBlock,
  TemplateContent,
  CustomFont,
} from "@aswin.dev/types";
import { isSection, isCustomBlock } from "@aswin.dev/types";
import { RenderContext } from "./render-context";
import { renderBlock } from "./renderers";
import { escapeHtml, escapeAttr } from "./escape";

export interface RenderOptions {
  customFonts?: CustomFont[];
  defaultFallbackFont?: string;
  allowHtmlBlocks?: boolean;
  /**
   * Resolves custom blocks to their HTML representation. Called once per
   * custom block in the content tree before MJML rendering. The renderer
   * has no built-in knowledge of how to render custom blocks; consumers
   * provide this function.
   *
   * Editor consumers: pass `editor.renderCustomBlock`.
   *
   * Headless consumers (Node.js, server, CLI): provide your own resolver,
   * typically using the same liquid template + field values pipeline as
   * the editor uses. If omitted, custom blocks fall back to
   * `block.renderedHtml` (if present) and otherwise are omitted from the
   * output.
   */
  renderCustomBlock?: (block: CustomBlock) => Promise<string>;
}

/**
 * Render template content to an MJML string.
 *
 * The function is async because resolving custom blocks may require
 * asynchronous work (e.g., the editor's liquid renderer dynamically imports
 * `liquidjs`). When the content has no custom blocks or `renderCustomBlock`
 * is omitted, no async work is performed but the function still resolves
 * synchronously — i.e., it always returns a Promise.
 */
export async function renderToMjml(
  content: TemplateContent,
  options?: RenderOptions,
): Promise<string> {
  const customFonts = options?.customFonts ?? [];
  const defaultFallbackFont =
    options?.defaultFallbackFont ?? "Arial, sans-serif";
  const allowHtmlBlocks = options?.allowHtmlBlocks ?? true;

  const customBlockHtml = await resolveCustomBlocks(
    content,
    options?.renderCustomBlock,
  );

  const renderContext = new RenderContext(
    content.settings.width,
    customFonts,
    defaultFallbackFont,
    allowHtmlBlocks,
    customBlockHtml,
  );

  const fontFamily = renderContext.resolveFontFamily(
    content.settings.fontFamily,
  );
  const backgroundColor = content.settings.backgroundColor;

  const multiCanvas = (content.canvasPages?.length ?? 0) > 0;

  const bodyContent = buildMjBodyContent(
    content,
    renderContext,
    allowHtmlBlocks,
  );

  const fontDeclarations = generateFontDeclarations(customFonts);
  const previewTag = generatePreviewTag(content.settings.preheaderText);

  const lang = escapeAttr(content.settings.locale);

  const mjBodyAttrs =
    `width="${renderContext.containerWidth}px" background-color="${backgroundColor}"` +
    (multiCanvas ? ` css-class="tpl-canvas-multi"` : "");

  return `<mjml lang="${lang}">
  <mj-head>${previewTag}
    <mj-attributes>
      <mj-all font-family="${fontFamily}" />
      <mj-text font-size="14px" />
      <mj-section padding="0" />
      <mj-column padding="0" />
      <mj-image fluid-on-mobile="true" />
    </mj-attributes>${fontDeclarations}
    <mj-style>
      a { color: inherit; text-decoration: none; }
      @media only screen and (max-width: 480px) {
        .tpl-hide-mobile { display: none !important; mso-hide: all !important; }
      }
      @media only screen and (min-width: 481px) and (max-width: 768px) {
        .tpl-hide-tablet { display: none !important; mso-hide: all !important; }
      }
      @media only screen and (min-width: 769px) {
        .tpl-hide-desktop { display: none !important; mso-hide: all !important; }
      }${multiCanvas ? multiCanvasStepCss() : ""}
    </mj-style>
  </mj-head>
  <mj-body ${mjBodyAttrs}>
${bodyContent}${multiCanvas ? `\n${multiCanvasStepScriptMjRaw()}\n` : ""}
  </mj-body>
</mjml>`;
}

/**
 * Renders `<mj-body>` inner markup: either every multi-step canvas page
 * (each wrapped in `<mj-wrapper css-class="tpl-canvas-page …">`) or the flat
 * `content.blocks` tree when `canvasPages` is absent or empty.
 */
function buildMjBodyContent(
  content: TemplateContent,
  context: RenderContext,
  allowHtmlBlocks: boolean,
): string {
  const pages = content.canvasPages;
  if (pages?.length) {
    const chunks: string[] = [];
    for (let i = 0; i < pages.length; i++) {
      const pageBlocks = pages[i].blocks ?? [];
      const inner = renderBlocksTopLevel(pageBlocks, context, allowHtmlBlocks);
      if (inner.trim() === "") {
        continue;
      }
      const rawClasses =
        i === 0
          ? `tpl-canvas-page tpl-canvas-page-idx-${i} tpl-canvas-page--current`
          : `tpl-canvas-page tpl-canvas-page-idx-${i}`;
      chunks.push(
        `<mj-wrapper css-class="${escapeAttr(rawClasses)}" padding="0">\n${inner}\n</mj-wrapper>`,
      );
    }
    return chunks.join("\n");
  }

  return renderBlocksTopLevel(content.blocks, context, allowHtmlBlocks);
}

function renderBlocksTopLevel(
  blocks: Block[],
  context: RenderContext,
  allowHtmlBlocks: boolean,
): string {
  return filterHtmlBlocks(blocks, allowHtmlBlocks)
    .map((block) => renderTopLevelBlock(block, context))
    .filter((value) => value !== "")
    .join("\n");
}

/**
 * Render a top-level block. Sections are rendered directly,
 * non-section blocks are wrapped in a default section/column.
 */
function renderTopLevelBlock(block: Block, context: RenderContext): string {
  if (isSection(block)) {
    const rendered = renderBlock(block, context);
    return wrapWithDisplayCondition(block, rendered);
  }

  const content = renderBlock(block, context);
  const wrapped = wrapInSection(content);
  return wrapWithDisplayCondition(block, wrapped);
}

/**
 * Wrap rendered block content with display condition tags if present.
 */
function wrapWithDisplayCondition(block: Block, rendered: string): string {
  if (rendered === "") {
    return "";
  }

  const displayCondition = block.displayCondition;

  if (!displayCondition) {
    return rendered;
  }

  return (
    `<mj-raw>${displayCondition.before}</mj-raw>` +
    "\n" +
    rendered +
    "\n" +
    `<mj-raw>${displayCondition.after}</mj-raw>`
  );
}

/**
 * Wrap block content in a default mj-section/mj-column for non-section blocks.
 */
function wrapInSection(content: string): string {
  if (content === "") {
    return "";
  }

  return `<mj-section>
  <mj-column>
${content}
  </mj-column>
</mj-section>`;
}

function generatePreviewTag(preheaderText?: string): string {
  if (!preheaderText) {
    return "";
  }

  const trimmed = preheaderText.trim();

  if (trimmed === "") {
    return "";
  }

  const escaped = escapeHtml(trimmed);

  return `\n    <mj-preview>${escaped}</mj-preview>`;
}

function generateFontDeclarations(customFonts: CustomFont[]): string {
  if (customFonts.length === 0) {
    return "";
  }

  return customFonts
    .map(
      (font) =>
        `\n    <mj-font name="${escapeAttr(font.name)}" href="${escapeAttr(font.url)}" />`,
    )
    .join("");
}

/**
 * Filter out HTML blocks if they are not allowed.
 */
function filterHtmlBlocks(blocks: Block[], allowHtmlBlocks: boolean): Block[] {
  if (allowHtmlBlocks) {
    return blocks;
  }

  return blocks.filter((block) => block.type !== "html");
}

/**
 * Walk the content tree, collect every custom block, then resolve each in
 * parallel via the supplied callback. Returns a map keyed by block id that
 * the synchronous render pass reads from. If no callback is provided, returns
 * an empty map and the sync pass falls back to `block.renderedHtml`.
 *
 * Per-block failures bubble up — the caller decides whether to swallow or
 * rethrow. We don't replace failures with placeholders here because that's
 * a policy decision (the editor swallows; a strict CLI may want to fail).
 */
async function resolveCustomBlocks(
  content: TemplateContent,
  renderCustomBlock: RenderOptions["renderCustomBlock"],
): Promise<Map<string, string>> {
  const result = new Map<string, string>();

  if (!renderCustomBlock) {
    return result;
  }

  const customBlocks: CustomBlock[] = [];
  collectCustomBlocksFromContent(content, customBlocks);

  if (customBlocks.length === 0) {
    return result;
  }

  const rendered = await Promise.all(
    customBlocks.map((block) => renderCustomBlock(block)),
  );

  for (let index = 0; index < customBlocks.length; index++) {
    result.set(customBlocks[index].id, rendered[index]);
  }

  return result;
}

function collectCustomBlocks(blocks: Block[], out: CustomBlock[]): void {
  for (const block of blocks) {
    if (isCustomBlock(block)) {
      out.push(block);
      continue;
    }

    if (isSection(block)) {
      for (const column of block.children) {
        collectCustomBlocks(column, out);
      }
    }
  }
}

function collectCustomBlocksFromContent(
  content: TemplateContent,
  out: CustomBlock[],
): void {
  const pages = content.canvasPages;
  if (pages?.length) {
    for (const page of pages) {
      collectCustomBlocks(page.blocks ?? [], out);
    }
    return;
  }
  collectCustomBlocks(content.blocks, out);
}

/**
 * CSS for multi-step canvas export: hide all `.tpl-canvas-page` except
 * `.tpl-canvas-page--current` (set on the first step in MJML).
 */
function multiCanvasStepCss(): string {
  return `
      .tpl-canvas-multi .tpl-canvas-page { display: none !important; }
      .tpl-canvas-multi .tpl-canvas-page.tpl-canvas-page--current { display: block !important; }`;
}

/**
 * Moves `.tpl-canvas-page--current` when next/previous-step buttons are clicked.
 * MJML puts `css-class` on the wrapping `<td>`, so match `td.tpl-btn-*-step`
 * (and `a.tpl-btn-*-step` when present).
 */
function multiCanvasStepScriptMjRaw(): string {
  return `<mj-raw>
    <script>
      (function () {
        var root = document.body;
        if (!root || !root.classList.contains("tpl-canvas-multi")) return;
        root.addEventListener(
          "click",
          function (e) {
            var link = e.target.closest("a");
            if (!link || !root.contains(link)) return;
            var isNext =
              link.classList.contains("tpl-btn-next-step") ||
              !!link.closest("td.tpl-btn-next-step");
            var isPrev =
              link.classList.contains("tpl-btn-prev-step") ||
              !!link.closest("td.tpl-btn-prev-step");
            if (!isNext && !isPrev) return;
            e.preventDefault();
            e.stopPropagation();
            var pages = Array.prototype.slice.call(
              root.querySelectorAll(".tpl-canvas-page"),
            );
            var cur = root.querySelector(
              ".tpl-canvas-page.tpl-canvas-page--current",
            );
            var i = pages.indexOf(cur);
            if (i < 0) return;
            if (isNext) {
              if (i >= pages.length - 1) return;
              pages[i].classList.remove("tpl-canvas-page--current");
              pages[i + 1].classList.add("tpl-canvas-page--current");
            } else {
              if (i <= 0) return;
              pages[i].classList.remove("tpl-canvas-page--current");
              pages[i - 1].classList.add("tpl-canvas-page--current");
            }
          },
          true,
        );
      })();
    </script>
  </mj-raw>`;
}

// Re-export utilities for consumers
export { RenderContext } from "./render-context";
export { escapeHtml, escapeAttr, convertMergeTagsToValues } from "./escape";
export { isHiddenOnAll, getCssClassAttr, getCssClasses } from "./visibility";
export { getWidthPercentages, getWidthPixels } from "./columns";
export { toPaddingString } from "./padding";
export { SOCIAL_ICONS, generateSocialIconDataUri } from "./social-icons";
export { renderBlock } from "./renderers";
export type { BlockRenderer } from "./renderers/section";
